import React, { useState, useEffect } from "react";
import axios from "../../AxiosInstance";
import Sidebar from "../../components/Sidebar";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";

const AmbassadorRequest = () => {
  const location = useLocation();
  
  // Determine if we're on applications page (pending) or ambassadors list (approved)
  const isApplicationsPage = location.pathname.includes("ambassador") && 
                            !location.pathname.includes("ambassadors-list");
  const filterStatus = isApplicationsPage ? "pending" : "approved";
  const pageTitle = isApplicationsPage ? "Applications (users still not approved)" : "Ambassadors List";

  const [allAmbassadors, setAllAmbassadors] = useState([]);
  const [filteredAmbassadors, setFilteredAmbassadors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Get all ambassadors
  const getAmbassador = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get("api/v1/admin/getAllAmbassador", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (res.data.success) {
        // Sort by createdAt (newest first)
        const sorted = [...res.data.data].sort((a, b) => {
          const dateA = new Date(a.createdAt || a._id);
          const dateB = new Date(b.createdAt || b._id);
          return dateB - dateA; // Descending order (newest first)
        });
        setAllAmbassadors(sorted);
        // Filter based on status
        const filtered = sorted.filter(
          (amb) => amb.status === filterStatus
        );
        setFilteredAmbassadors(filtered);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch ambassadors");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter ambassadors when status changes
  useEffect(() => {
    if (allAmbassadors.length > 0) {
      // Filter based on status (already sorted by newest first)
      const filtered = allAmbassadors.filter(
        (amb) => amb.status === filterStatus
      );
      setFilteredAmbassadors(filtered);
      setCurrentPage(1); // Reset to first page when filter changes
    }
  }, [filterStatus, allAmbassadors]);

  // Update filter when route changes
  useEffect(() => {
    getAmbassador();
  }, [location.pathname]);

  // handleAccountStatus
  const handleAccountStatus = async (record, status) => {
    try {
      const res = await axios.post(
        "api/v1/admin/changeAccountStatus",
        { ambassadorId: record._id, userId: record.userId, status: status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (res.data.success) {
        toast.success(res.data.message);
        // Refresh data instead of reloading page
        getAmbassador();
      }
    } catch (error) {
      toast.error("Something Went Wrong");
    }
  };

  const handleDownloadExcel = () => {
    if (!Array.isArray(filteredAmbassadors) || filteredAmbassadors.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = filteredAmbassadors.map((item) => ({
      Name: item.userId?.name || "N/A",
      Email: item.userId?.email || "N/A",
      "Book Average":
        ["Up to 3", "Up to 6", "Up to 12", "More than 12"][item.bookavargae] ||
        "N/A",
      "You're":
        ["Student", "Worker", "Retired", "Other"][item.student] || "N/A",
      Genres: Array.isArray(item.genres) ? item.genres.join(", ") : "N/A",
      "Past Book": item.past === 0 ? "Yes" : "No",
      GuideLine: item.discussion || "N/A",
      Discussion: item.wishes || "N/A",
      "Event Count": item.eventCount
        ? `Book Club: ${item.eventCount.bookClub ?? 0}, Book Duo: ${
            item.eventCount.bookDuo ?? 0
          }, Silent Reading: ${item.eventCount.silentReading ?? 0}`
        : "N/A",
      Level: item.level ?? "N/A",
      Status: item.status || "N/A",
    }));
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ambassadors");
    XLSX.writeFile(wb, `${pageTitle}.xlsx`);
  };

  const getStatusBadgeClass = (status) => {
    if (status === "approved") {
      return "px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-[10px] font-semibold";
    } else if (status === "pending") {
      return "px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 text-[10px] font-semibold";
    } else {
      return "px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-semibold";
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredAmbassadors.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredAmbassadors.slice(startIndex, endIndex);

  // Helper functions for rendering
  const getBookAverage = (value) => {
    const bookAvarageOption = {
      0: "Up to 3",
      1: "Up to 6",
      2: "Up to 12",
      3: "More than 12",
    };
    return bookAvarageOption[value] || "N/A";
  };

  const getStudentOption = (value) => {
    const studentOptions = {
      0: "Student",
      1: "Worker",
      2: "Retired",
      3: "Other",
    };
    return studentOptions[value] || "Unknown";
  };

  const getPastBook = (value) => {
    const pastBookOption = {
      0: "Yes",
      1: "No",
    };
    return pastBookOption[value] || "N/A";
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-800">{pageTitle}</h1>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden p-6">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#34c3c9]"></div>
                <p className="mt-2 text-gray-600">Loading ambassadors...</p>
              </div>
            </div>
          ) : filteredAmbassadors.length > 0 ? (
            <>
              {/* Table Container - Compact, no horizontal scroll */}
              <div className="flex-1 overflow-y-auto min-h-0 bg-white rounded-lg shadow-sm border border-gray-200">
                <table className="w-full divide-y divide-gray-200 border-collapse text-xs">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Book Avg
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Role
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Genres
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Past
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Guide
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Discuss
                      </th>
                      <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">
                        Events
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Level
                      </th>
                      <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentData.map((record) => (
                      <tr key={record._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 py-1.5 text-xs font-medium text-gray-900 truncate max-w-[100px]">
                          {record.userId?.name || "N/A"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-500 truncate max-w-[140px]" title={record.userId?.email || "N/A"}>
                          {record.userId?.email || "N/A"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {getBookAverage(record.bookavargae)}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {getStudentOption(record.student)}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 truncate max-w-[100px]" title={Array.isArray(record.genres) && record.genres.length > 0 ? record.genres.join(", ") : "N/A"}>
                          {Array.isArray(record.genres) && record.genres.length > 0
                            ? record.genres.length > 1
                              ? `${record.genres.slice(0, 1).join(", ")}...`
                              : record.genres.join(", ")
                            : "N/A"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {getPastBook(record.past)}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 truncate max-w-[100px]" title={record.discussion || "N/A"}>
                          {record.discussion ? (record.discussion.length > 20 ? `${record.discussion.substring(0, 20)}...` : record.discussion) : "N/A"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 truncate max-w-[100px]" title={record.wishes || "N/A"}>
                          {record.wishes ? (record.wishes.length > 20 ? `${record.wishes.substring(0, 20)}...` : record.wishes) : "N/A"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 text-center">
                          <div className="flex gap-0.5 justify-center flex-wrap">
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800" title="Book Club">
                              {record.eventCount?.bookClub ?? 0}
                            </span>
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800" title="Book Duo">
                              {record.eventCount?.bookDuo ?? 0}
                            </span>
                            <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800" title="Silent Reading">
                              {record.eventCount?.silentReading ?? 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {record.level ?? "N/A"}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={getStatusBadgeClass(record.status)}>
                            {record.status?.toUpperCase() || "N/A"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-center">
                          <div className="flex gap-0.5 justify-center">
                            {record.status === "pending" ? (
                              <>
                                <button
                                  className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
                                  onClick={() => handleAccountStatus(record, "approved")}
                                  title="Approve"
                                >
                                  ✓
                                </button>
                                <button
                                  className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                                  onClick={() => handleAccountStatus(record, "rejected")}
                                  title="Reject"
                                >
                                  ✕
                                </button>
                              </>
                            ) : record.status === "approved" ? (
                              <button
                                className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                                onClick={() => handleAccountStatus(record, "rejected")}
                                title="Reject"
                              >
                                ✕
                              </button>
                            ) : (
                              <button
                                className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
                                onClick={() => handleAccountStatus(record, "approved")}
                                title="Approve"
                              >
                                ✓
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination and Download */}
              <div className="flex justify-between items-center mt-4 flex-shrink-0">
                {/* Pagination */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      
                      // Always show first page
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setCurrentPage(1)}
                          className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                            currentPage === 1
                              ? "bg-[#34c3c9] text-white"
                              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          1
                        </button>
                      );

                      // Show ellipsis if current page is far from start
                      if (currentPage > 3) {
                        pages.push(
                          <span key="ellipsis-start" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      // Show pages around current page
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                currentPage === i
                                  ? "bg-[#34c3c9] text-white"
                                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }
                      }

                      // Show ellipsis if current page is far from end
                      if (currentPage < totalPages - 2) {
                        pages.push(
                          <span key="ellipsis-end" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      // Always show last page (if more than 1 page)
                      if (totalPages > 1) {
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                              currentPage === totalPages
                                ? "bg-[#34c3c9] text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                  
                  <span className="text-sm text-gray-500 ml-2">
                    ({filteredAmbassadors.length} total)
                  </span>
                </div>

                {/* Download Button */}
                <button
                  className="px-6 py-2 text-white font-semibold rounded-lg bg-[#34c3c9] hover:bg-[#2aa8ad] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDownloadExcel}
                  disabled={filteredAmbassadors.length === 0}
                >
                  Download Excel
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500 text-lg">
                {isApplicationsPage
                  ? "No pending applications found"
                  : "No approved ambassadors found"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmbassadorRequest;
